import type { ReactNode, SyntheticEvent } from 'react';
import React from 'react';
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Provider, Root, Viewport } from '@radix-ui/react-toast';
import { styled } from '@slicknode/stylemapper';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  faClock,
  faInfoCircle,
  faTriangleExclamation,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { faCircleCheck } from '@soundxyz/font-awesome/sharp-solid-svg-icons/index.mjs';
import { ModalContext } from '@context/modal';
import { IconButton } from '../icon-buttons/IconButton';
import { AccessibleLink } from '../link/AccessibleLink';

const TOAST_DURATION = 3000;

export const ToastVariant = {
  PRIMARY: 'primary',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  PENDING: 'pending',
} as const;

export interface ToastProps {
  alignment?: 'left' | 'right';
  ctaOnClick?: (event: SyntheticEvent) => void;
  ctaHref?: string;
  ctaText?: string;
  duration?: number;
  isToastOpen: boolean;
  setToastOpen: (isOpen: boolean) => void;
  text: ReactNode;
  variant?: (typeof ToastVariant)[keyof typeof ToastVariant];
  withAudioPlayer?: boolean;
  inverted?: boolean;
  withExpandedAudioPlayer?: boolean;
}

const CtaElement = ({
  ctaHref,
  ctaText,
  ctaOnClick,
}: {
  ctaHref?: string;
  ctaText: string;
  ctaOnClick: (e: SyntheticEvent<Element, Event>) => void;
}) => {
  return ctaHref ? (
    <AccessibleLink
      className="text-brand300 hover:text-brand600"
      absoluteUri={ctaHref}
      type="external"
    >
      {ctaText}
    </AccessibleLink>
  ) : (
    <button className="text-brand300 hover:text-brand600" onClick={ctaOnClick}>
      {ctaText}
    </button>
  );
};

export const Toast = ({
  alignment = 'right',
  ctaOnClick = () => null,
  ctaHref,
  ctaText,
  duration = TOAST_DURATION,
  isToastOpen,
  setToastOpen,
  text,
  variant = ToastVariant.PRIMARY,
  withAudioPlayer = false,
  inverted = false,
  withExpandedAudioPlayer = false,
}: ToastProps) => {
  let variantIcon: FontAwesomeIconProps['icon'];
  let variantIconColor: string;

  switch (variant) {
    case ToastVariant.SUCCESS:
      variantIcon = faCircleCheck;
      variantIconColor = '#34D399';
      break;
    case ToastVariant.WARNING:
      variantIcon = faTriangleExclamation;
      variantIconColor = '#FBBF24';
      break;
    case ToastVariant.ERROR:
      variantIcon = faTriangleExclamation;
      variantIconColor = '#EF4444';
      break;
    case ToastVariant.PENDING:
      variantIcon = faClock;
      variantIconColor = '#EAB308';
      break;
    case ToastVariant.PRIMARY:
    default:
      variantIcon = faInfoCircle;
      variantIconColor = inverted ? '#0E1213' : '#fff';
      break;
  }
  const hasAudioPlayer = withAudioPlayer && !withExpandedAudioPlayer;
  const { activeModal } = ModalContext.useContainer();
  const hasActiveModal = !!activeModal;
  return (
    <Provider>
      <ToastContainer
        open={isToastOpen}
        duration={duration}
        onOpenChange={setToastOpen}
        inverted={inverted}
      >
        <FontAwesomeIcon icon={variantIcon} color={variantIconColor} size="xl" />
        <div>
          <p>{text}</p>
          {!!ctaText ? <br /> : ' '}
          {ctaText && <CtaElement ctaHref={ctaHref} ctaText={ctaText} ctaOnClick={ctaOnClick} />}
        </div>
        <IconButton
          variant={inverted ? 'primary' : 'inverted'}
          onClick={() => setToastOpen(false)}
          iconSize={16}
          icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
          css={{
            flexShrink: 0,
          }}
        />
      </ToastContainer>
      <ToastViewport
        withExpandedAudioPlayer={withExpandedAudioPlayer}
        withAudioPlayer={hasAudioPlayer}
        alignment={alignment}
        hasActiveModal={hasActiveModal}
      />
    </Provider>
  );
};

const ToastContainer = styled(
  Root,
  'flex justify-between gap-[18px] shadow-secondary rounded-md font-base text-base-m font-medium mb-3 p-4 mx-3 items-center [&_div]:w-full [&_p]:inline [&_p]:overflow-hidden md:mx-10 md:mb-10 md:text-base-l data-[state=open]:animate-slideIn motion-reduce:data-[state=close]:animate-hide transition-transform duration-150 ease-in-out',
  {
    variants: {
      inverted: {
        true: 'bg-white text-neutral900 [&_p]:text-neutral900',
        false: 'bg-base900 text-white [&_p]:text-white',
      },
    },
  },
);

const ToastViewport = styled(
  Viewport,
  'fixed list-none z-toast outline-none w-full md:bottom-0 md:w-[480px]',
  {
    variants: {
      alignment: {
        right: 'right-0',
        left: 'left-0',
      },
      withAudioPlayer: {
        true: 'bottom-[calc(118px_+_env(safe-area-inset-bottom))] md:bottom-0',
        false: 'bottom-[calc(68px_+_env(safe-area-inset-bottom))] md:bottom-0',
      },
      withExpandedAudioPlayer: {
        true: '!bottom-0',
      },
      hasActiveModal: {
        true: '!bottom-[env(safe-area-inset-bottom)] md:bottom-0',
        false: '',
      },
    },
  },
);
